import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageUploadBox, ImageUploader } from '@service/seller/components/ImageUploadBox';
import {
  simpleBugoBrandListDataAtom,
  simpleBugoBrandListStatusAtom,
} from '@shared/api/bugoBrand/bugoBrand.atom';
import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import {
  giftItemDataAtomFamily,
  giftItemListDataAtom,
  giftItemListStatusAtom,
  giftItemStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import {
  createGiftItem,
  deleteGiftItem,
  updateGiftItem,
} from '@shared/api/giftItem/giftItem.controller';
import { useGiftItemHook, useGiftItemListHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemCreate, GiftItemUpdate } from '@shared/api/giftItem/giftItem.interface';
import {
  giftItemInfoListDataAtom,
  giftItemInfoListStatusAtom,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import { useGiftItemInfoListHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import { GiftItemInfoData } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useNavigate, useParams } from 'react-router-dom';

interface FormData {
  giftItemInfo: string;
  bugoBrand: string;
  giftDeliveryCategory: string; // 배송 카테고리 ID (오버라이드)
  name: string;
  priceRegular: number;
  priceRetail: number;
  priceWhole: number;
  priceRebate: number;
  isTaxable: boolean;
  detailInfo: string;
  order: number | ''; // 표시 순서 오버라이드
}

export const GiftItemFormPage: FC = function GiftItemFormPage() {
  const { itemId } = useParams<{ itemId?: string }>();
  const isEdit = itemId && itemId !== 'new';
  const navigate = useNavigate();

  const { sellerRole } = useAuth();

  // Hooks
  useGiftItemInfoListHook(sellerRole());
  useGiftItemHook(isEdit ? itemId ?? '' : '', sellerRole());
  useSimpleBugoBrandListHook();
  useGiftDeliveryCategoryListHook(sellerRole());
  useGiftItemListHook(sellerRole()); // 중복 체크용

  // State
  const giftItemInfoListStatus = useRecoilValue(giftItemInfoListStatusAtom);
  const giftItemInfoList = useRecoilValue(giftItemInfoListDataAtom);

  const giftItemStatus = useRecoilValue(giftItemStatusAtomFamily(itemId ?? ''));
  const giftItemData = useRecoilValue(giftItemDataAtomFamily(itemId ?? ''));

  const bugoBrandListStatus = useRecoilValue(simpleBugoBrandListStatusAtom);
  const bugoBrandList = useRecoilValue(simpleBugoBrandListDataAtom);

  const deliveryCategoryListStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  // 전체 GiftItem 목록 (중복 체크용)
  const giftItemListStatus = useRecoilValue(giftItemListStatusAtom);
  const giftItemListData = useRecoilValue(giftItemListDataAtom);

  const [formData, setFormData] = useState<FormData>({
    giftItemInfo: '',
    bugoBrand: '',
    giftDeliveryCategory: '',
    name: '',
    priceRegular: 0,
    priceRetail: 0,
    priceWhole: 0,
    priceRebate: 0,
    isTaxable: false,
    detailInfo: '',
    order: '',
  });

  // 이미지 관련 state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [headerImageFiles, setHeaderImageFiles] = useState<File[] | null>(null);
  const [headerImagePreviews, setHeaderImagePreviews] = useState<string[]>([]);
  const [detailImageFiles, setDetailImageFiles] = useState<File[] | null>(null);
  const [detailImagePreviews, setDetailImagePreviews] = useState<string[]>([]);
  const [showOverride, setShowOverride] = useState(false);

  const [loading, setLoading] = useState(false);

  // 모달 상태
  const openId = `giftItem-${itemId ?? 'new'}`;
  const setHeaderImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`headerImageUploader-${openId}`),
  );
  const setDetailImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${openId}`),
  );

  // 수정 모드일 때 데이터 로드
  useEffect(() => {
    if (isEdit && giftItemStatus === 'success' && giftItemData) {
      setFormData({
        giftItemInfo:
          typeof giftItemData?.giftItemInfo === 'object'
            ? giftItemData?.giftItemInfo?._id
            : giftItemData?.giftItemInfo,
        bugoBrand:
          typeof giftItemData?.bugoBrand === 'object'
            ? giftItemData?.bugoBrand?._id
            : giftItemData?.bugoBrand,
        giftDeliveryCategory:
          typeof giftItemData?.giftDeliveryCategory === 'object'
            ? giftItemData?.giftDeliveryCategory?._id || ''
            : giftItemData?.giftDeliveryCategory || '',
        name: giftItemData?.name || '',
        priceRegular: giftItemData.priceRegular || 0,
        priceRetail: giftItemData.priceRetail || 0,
        priceWhole: giftItemData.priceWhole || 0,
        priceRebate: giftItemData.priceRebate || 0,
        isTaxable: giftItemData.isTaxable ?? false,
        detailInfo: giftItemData.detailInfo || '',
        order: giftItemData.order ?? '',
      });
      // 오버라이드 데이터가 있으면 섹션 펼치기
      if (
        giftItemData.name ||
        giftItemData.image ||
        giftItemData.headerImages?.length ||
        giftItemData.detailImages?.length ||
        giftItemData.detailInfo ||
        giftItemData.order !== undefined ||
        giftItemData.giftDeliveryCategory
      ) {
        setShowOverride(true);
      }
    }
  }, [isEdit, giftItemStatus, giftItemData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (['priceRegular', 'priceRetail', 'priceWhole', 'priceRebate'].includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
    } else if (name === 'order') {
      setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const onHeaderImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setHeaderImageFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setHeaderImagePreviews(previews);
    setHeaderImageUploaderOpen(false);
  };

  const onDetailImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setDetailImageFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setDetailImagePreviews(previews);
    setDetailImageUploaderOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.giftItemInfo) {
      alert('원상품을 선택해주세요.');
      return;
    }

    if (!formData.bugoBrand) {
      alert('브랜드를 선택해주세요.');
      return;
    }

    // 중복 체크
    if (isDuplicate) {
      alert('해당 상조에 이미 동일한 원상품이 등록되어 있습니다.');
      return;
    }

    setLoading(true);

    try {
      let imageId: string | undefined;
      let headerImageIds: string[] | undefined;
      let detailImageIds: string[] | undefined;

      // 대표 이미지 업로드
      if (imageFile) {
        const uploadedImage = await postImage(imageFile, sellerRole());
        imageId = uploadedImage._id;
      }

      // 헤더 이미지 업로드 (순서 유지)
      if (headerImageFiles && headerImageFiles.length > 0) {
        headerImageIds = [];
        for (const file of headerImageFiles) {
          const uploaded = await postImage(file, sellerRole());
          headerImageIds.push(uploaded._id);
        }
      }

      // 상세 이미지 업로드 (순서 유지)
      if (detailImageFiles && detailImageFiles.length > 0) {
        detailImageIds = [];
        for (const file of detailImageFiles) {
          const uploaded = await postImage(file, sellerRole());
          detailImageIds.push(uploaded._id);
        }
      }

      if (isEdit) {
        const updateData: GiftItemUpdate = {
          _id: itemId,
          giftItemInfo: formData.giftItemInfo,
          bugoBrand: formData.bugoBrand,
          giftDeliveryCategory: formData.giftDeliveryCategory || null,
          name: formData.name || undefined,
          priceRegular: formData.priceRegular,
          priceRetail: formData.priceRetail,
          priceWhole: formData.priceWhole,
          priceRebate: formData.priceRebate,
          isTaxable: formData.isTaxable,
          detailInfo: formData.detailInfo || undefined,
          order: formData.order !== '' ? formData.order : undefined,
          image: imageId,
          headerImages: headerImageIds,
          detailImages: detailImageIds,
        };
        await updateGiftItem(updateData, sellerRole());
        alert('상품이 수정되었습니다.');
      } else {
        const createData: GiftItemCreate = {
          giftItemInfo: formData.giftItemInfo,
          bugoBrand: formData.bugoBrand,
          giftDeliveryCategory: formData.giftDeliveryCategory || undefined,
          name: formData.name || undefined,
          priceRegular: formData.priceRegular,
          priceRetail: formData.priceRetail,
          priceWhole: formData.priceWhole,
          priceRebate: formData.priceRebate,
          isTaxable: formData.isTaxable,
          detailInfo: formData.detailInfo || undefined,
          order: formData.order !== '' ? formData.order : undefined,
          image: imageId,
          headerImages: headerImageIds,
          detailImages: detailImageIds,
        };
        await createGiftItem(createData, sellerRole());
        alert('상품이 등록되었습니다.');
      }
      navigate('/gift-seller/item');
    } catch (error) {
      console.error(error);
      alert('저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;

    if (!confirm('정말 삭제하시겠습니까?')) return;

    setLoading(true);
    try {
      await deleteGiftItem(itemId ?? '', sellerRole());
      alert('상품이 삭제되었습니다.');
      navigate('/gift-seller/item');
    } catch (error) {
      console.error(error);
      alert('삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const giftItemInfoOptions = useMemo(() => {
    if (giftItemInfoListStatus === 'success' && giftItemInfoList) {
      return giftItemInfoList.filter((info: GiftItemInfoData) => info.isEnabled);
    }
    return [];
  }, [giftItemInfoListStatus, giftItemInfoList]);

  // 선택된 원상품 정보
  const selectedGiftItemInfo = useMemo(() => {
    if (formData.giftItemInfo && giftItemInfoList) {
      return giftItemInfoList.find(
        (info: GiftItemInfoData) => info._id === formData.giftItemInfo,
      );
    }
    return null;
  }, [formData.giftItemInfo, giftItemInfoList]);

  // 원상품 선택 시 가격 기본값 설정 (수정 모드가 아닐 때만)
  useEffect(() => {
    if (!isEdit && selectedGiftItemInfo) {
      setFormData((prev) => ({
        ...prev,
        priceRegular: selectedGiftItemInfo.priceRegular || 0,
        priceRetail: selectedGiftItemInfo.priceRetail || 0,
        priceWhole: selectedGiftItemInfo.priceWhole || 0,
        isTaxable: selectedGiftItemInfo.isTaxable ?? false,
      }));
    }
  }, [isEdit, selectedGiftItemInfo]);

  // 중복 체크: 해당 브랜드에 동일한 원상품이 이미 있는지
  const isDuplicate = useMemo(() => {
    if (
      !isEdit &&
      giftItemListStatus === 'success' &&
      giftItemListData &&
      formData.giftItemInfo &&
      formData.bugoBrand
    ) {
      return giftItemListData.some((item) => {
        const brandId =
          typeof item.bugoBrand === 'object' ? item.bugoBrand?._id : item.bugoBrand;
        const itemInfoId =
          typeof item.giftItemInfo === 'object'
            ? item.giftItemInfo?._id
            : item.giftItemInfo;
        return brandId === formData.bugoBrand && itemInfoId === formData.giftItemInfo;
      });
    }
    return false;
  }, [
    isEdit,
    giftItemListStatus,
    giftItemListData,
    formData.giftItemInfo,
    formData.bugoBrand,
  ]);

  // 기존 대표 이미지 URL (오버라이드된 것)
  const defaultImageUrl = useMemo(() => {
    if (giftItemData?.image && typeof giftItemData.image === 'object') {
      return (giftItemData.image as Image).url;
    }
    return undefined;
  }, [giftItemData]);

  // 기존 헤더 이미지 목록 (오버라이드된 것)
  const existingHeaderImages = useMemo(() => {
    if (giftItemData?.headerImages && Array.isArray(giftItemData.headerImages)) {
      return giftItemData.headerImages as Image[];
    }
    return [];
  }, [giftItemData]);

  // 기존 상세 이미지 목록 (오버라이드된 것)
  const existingDetailImages = useMemo(() => {
    if (giftItemData?.detailImages && Array.isArray(giftItemData.detailImages)) {
      return giftItemData.detailImages as Image[];
    }
    return [];
  }, [giftItemData]);

  if (isEdit && giftItemStatus !== 'success') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800"
          >
            ←
          </button>
          <h1 className="text-xl font-bold">{isEdit ? '상품 수정' : '상품 등록'}</h1>
        </div>
        {isEdit && (
          <button
            onClick={handleDelete}
            className="rounded border border-red-300 px-4 py-1.5 text-sm font-medium text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
            disabled={loading}
          >
            삭제
          </button>
        )}
      </div>

      {/* Form */}
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* 왼쪽: 기본 정보 */}
            <div className="space-y-4">
              {/* 원상품 선택 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  원상품 선택 <span className="text-red-500">*</span>
                </label>
                <select
                  name="giftItemInfo"
                  value={formData.giftItemInfo}
                  onChange={handleChange}
                  className="w-full rounded border px-3 py-2 text-sm"
                  required
                >
                  <option value="">원상품을 선택하세요</option>
                  {giftItemInfoOptions.map((info: GiftItemInfoData) => (
                    <option key={info._id} value={info._id}>
                      [{info.type === 'Delivery' ? '배송' : '쿠폰'}] {info.name} (
                      {info.priceWhole?.toLocaleString()}원)
                    </option>
                  ))}
                </select>

                {/* 선택된 원상품 미리보기 */}
                {selectedGiftItemInfo && (
                  <div className="mt-2 flex items-center gap-2 rounded bg-gray-50 p-2">
                    {selectedGiftItemInfo.image &&
                      typeof selectedGiftItemInfo.image === 'object' && (
                        <img
                          src={fileBucketUrl(selectedGiftItemInfo.image.url, 'raw')}
                          alt={selectedGiftItemInfo.name}
                          className="h-10 w-10 rounded object-cover"
                        />
                      )}
                    <div className="text-xs">
                      <p className="font-medium">{selectedGiftItemInfo.name}</p>
                      <p className="text-gray-500">
                        정가: {selectedGiftItemInfo.priceRegular?.toLocaleString() || 0}원
                        → 판매가:{' '}
                        {selectedGiftItemInfo.priceRetail?.toLocaleString() || 0}원 /
                        도매가: {selectedGiftItemInfo.priceWhole?.toLocaleString()}원
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* 브랜드 선택 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  상조 브랜드 <span className="text-red-500">*</span>
                </label>
                <select
                  name="bugoBrand"
                  value={formData.bugoBrand}
                  onChange={handleChange}
                  className="w-full rounded border px-3 py-2 text-sm"
                  required
                  disabled={bugoBrandListStatus !== 'success'}
                >
                  <option value="">
                    {bugoBrandListStatus === 'success'
                      ? '상조를 선택하세요'
                      : '로딩중...'}
                  </option>
                  {bugoBrandListStatus === 'success' &&
                    bugoBrandList?.map((brand: BugoBrand) => (
                      <option key={brand._id} value={brand._id}>
                        {brand.bugoAgency?.corpName || brand._id}
                      </option>
                    ))}
                </select>

                {/* 중복 경고 */}
                {isDuplicate && (
                  <div className="mt-2 rounded bg-red-50 p-2 text-sm text-red-600">
                    ⚠️ 해당 상조에 이미 동일한 원상품이 등록되어 있습니다.
                  </div>
                )}
              </div>

              {/* 가격 설정 - 한 줄에 4개 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  가격 설정 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">정가</label>
                    <input
                      type="number"
                      name="priceRegular"
                      value={formData.priceRegular}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">할인가</label>
                    <input
                      type="number"
                      name="priceRetail"
                      value={formData.priceRetail}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">도매가</label>
                    <input
                      type="number"
                      name="priceWhole"
                      value={formData.priceWhole}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">리베이트</label>
                    <input
                      type="number"
                      name="priceRebate"
                      value={formData.priceRebate}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* 과세 여부 */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isTaxable"
                  checked={formData.isTaxable}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, isTaxable: e.target.checked }))
                  }
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="isTaxable" className="text-sm font-medium text-gray-700">
                  과세 상품
                </label>
                {selectedGiftItemInfo && (
                  <span className="text-xs text-gray-400">
                    (원상품: {selectedGiftItemInfo.isTaxable ? '과세' : '비과세'})
                  </span>
                )}
              </div>
            </div>

            {/* 오른쪽: 오버라이드 섹션 */}
            <div>
              <button
                type="button"
                onClick={() => setShowOverride(!showOverride)}
                className="flex w-full items-center justify-between rounded border bg-gray-50 px-3 py-2 text-sm"
              >
                <span className="font-medium text-gray-600">
                  상품 정보 오버라이드 (선택)
                </span>
                <span className="text-gray-400">{showOverride ? '▲' : '▼'}</span>
              </button>

              {showOverride && (
                <div className="mt-3 space-y-3 rounded border bg-gray-50 p-3">
                  {/* 상품명 (오버라이드) */}
                  <div>
                    <label className="mb-0.5 block text-xs font-medium text-gray-600">
                      상품명 오버라이드
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="원상품명을 덮어쓰려면 입력"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>

                  {/* 배송 카테고리 (오버라이드) */}
                  <div>
                    <label className="mb-0.5 block text-xs font-medium text-gray-600">
                      배송 카테고리 오버라이드
                    </label>
                    <select
                      name="giftDeliveryCategory"
                      value={formData.giftDeliveryCategory}
                      onChange={handleChange}
                      disabled={deliveryCategoryListStatus !== 'success'}
                      className="w-full rounded border px-2 py-1.5 text-sm"
                    >
                      <option value="">선택 안함 (원상품 카테고리 사용)</option>
                      {deliveryCategoryList.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-0.5 text-[10px] text-gray-400">
                      설정 시 원상품의 배송 카테고리보다 우선 적용됩니다.
                    </p>
                  </div>

                  {/* 표시 순서 (오버라이드) */}
                  <div>
                    <label className="mb-0.5 block text-xs font-medium text-gray-600">
                      표시 순서 오버라이드
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                      placeholder="미설정 시 원상품 순서 사용"
                      className="w-full rounded border px-2 py-1.5 text-sm"
                      min={0}
                    />
                    <p className="mt-0.5 text-[10px] text-gray-400">
                      설정하면 원상품 순서보다 우선 (낮을수록 앞에 표시)
                    </p>
                  </div>

                  {/* 대표 이미지 (오버라이드) */}
                  <div className="max-w-[300px]">
                    <ImageUploadBox
                      title="대표 이미지 오버라이드"
                      setFile={setImageFile}
                      defaultImageUrl={defaultImageUrl}
                    />
                  </div>

                  {/* 헤더 이미지 (오버라이드) */}
                  <div className="max-w-[300px]">
                    <label className="mb-0.5 block text-xs font-medium text-gray-600">
                      헤더 이미지 오버라이드 (정사각형, carousel용)
                    </label>
                    <p className="mb-1 text-[10px] text-gray-400">순서대로 정렬됩니다</p>
                    <Button
                      type="button"
                      className="rounded bg-blue-600 px-2 py-1 text-xs text-white"
                      onClick={() => setHeaderImageUploaderOpen(true)}
                    >
                      이미지 업로드
                    </Button>

                    {/* 미리보기 또는 기존 이미지 */}
                    {headerImagePreviews.length > 0 ? (
                      <div className="mt-2 flex gap-1.5 overflow-x-auto">
                        {headerImagePreviews.map((preview, idx) => (
                          <div key={idx} className="relative flex-shrink-0">
                            <img
                              src={preview}
                              alt={`헤더이미지 ${idx + 1}`}
                              className="h-14 w-14 rounded object-cover"
                            />
                            <span className="absolute left-0.5 top-0.5 rounded bg-black/50 px-0.5 text-[10px] text-white">
                              {idx + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : existingHeaderImages.length > 0 ? (
                      <div className="mt-2 flex gap-1.5 overflow-x-auto">
                        {existingHeaderImages.map((img, idx) => (
                          <div key={idx} className="relative flex-shrink-0">
                            <img
                              src={fileBucketUrl(img.url, 'raw')}
                              alt={`헤더이미지 ${idx + 1}`}
                              className="h-14 w-14 rounded object-cover"
                            />
                            <span className="absolute left-0.5 top-0.5 rounded bg-black/50 px-0.5 text-[10px] text-white">
                              {idx + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {/* 상세 이미지 (오버라이드) */}
                  <div className="max-w-[300px]">
                    <label className="mb-0.5 block text-xs font-medium text-gray-600">
                      상세 이미지 오버라이드 (세로로 이어붙임)
                    </label>
                    <p className="mb-1 text-[10px] text-gray-400">순서대로 정렬됩니다</p>
                    <Button
                      type="button"
                      className="rounded bg-indigo-600 px-2 py-1 text-xs text-white"
                      onClick={() => setDetailImageUploaderOpen(true)}
                    >
                      이미지 업로드
                    </Button>

                    {/* 미리보기 또는 기존 이미지 */}
                    {detailImagePreviews.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {detailImagePreviews.map((preview, idx) => (
                          <div key={idx} className="relative">
                            <img
                              src={preview}
                              alt={`상세이미지 ${idx + 1}`}
                              className="w-full rounded object-cover"
                            />
                            <span className="absolute left-0.5 top-0.5 rounded bg-black/50 px-0.5 text-[10px] text-white">
                              {idx + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : existingDetailImages.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {existingDetailImages.map((img, idx) => (
                          <div key={idx} className="relative">
                            <img
                              src={fileBucketUrl(img.url, 'w300')}
                              alt={`상세이미지 ${idx + 1}`}
                              className="w-full rounded object-cover"
                            />
                            <span className="absolute left-0.5 top-0.5 rounded bg-black/50 px-0.5 text-[10px] text-white">
                              {idx + 1}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {/* 상세 설명 (오버라이드) */}
                  <div>
                    <label className="mb-0.5 block text-xs font-medium text-gray-600">
                      상세 설명 오버라이드
                    </label>
                    <textarea
                      name="detailInfo"
                      value={formData.detailInfo}
                      onChange={handleChange}
                      placeholder="상세 설명을 입력하세요"
                      className="h-16 w-full rounded border px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 제출 버튼 */}
          <div className="mt-6 border-t pt-4">
            <Button
              type="button"
              disabled={loading}
              onClick={handleSubmit}
              className="rounded bg-blue-600 px-6 py-2 font-medium text-white disabled:bg-gray-400"
            >
              {loading ? '저장 중...' : isEdit ? '수정하기' : '등록하기'}
            </Button>
          </div>
        </div>
      </div>

      {/* 헤더 이미지 업로더 모달 */}
      <ImageUploader
        openId={`headerImageUploader-${openId}`}
        title="헤더 이미지 업로드 (정사각형)"
        onSubmit={onHeaderImageUploaderFunc}
        useName={false}
      />

      {/* 상세 이미지 업로더 모달 */}
      <ImageUploader
        openId={openId}
        title="상세이미지 업로드"
        onSubmit={onDetailImageUploaderFunc}
        useName={false}
      />
    </div>
  );
};
